"use client";

import clsx from "clsx";
import Image from "next/image";
import { createContext, useContext, useRef, useState } from "react";
import { Transition } from "react-transition-group";

type SnackbarVariant = "success" | "alert";

type SnackbarContextValue = {
  showSnackbar: (args: {
    message: string;
    variant?: SnackbarVariant;
    duration?: number;
  }) => void;
  hideSnackbar: () => void;
};

const SnackbarContext = createContext<SnackbarContextValue | undefined>(
  undefined,
);

function Snackbar({
  open,
  message,
  variant,
  hideSnackbar,
}: {
  open: boolean;
  message: string;
  variant: SnackbarVariant;
  hideSnackbar: () => void;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const duration = 400;

  const defaultStyle = {
    transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`,
    opacity: 0,
    transform: "translateY(-20px)",
  };

  const transitionStyles = {
    entering: { opacity: 1, transform: "translateY(0)" },
    entered: { opacity: 1, transform: "translateY(0)" },
    exiting: {
      opacity: 0,
      transition: `opacity ${duration}ms ease-in, transform ${duration}ms ease-in`,
      transform: "translateY(0)",
    },
    exited: { opacity: 0 },
    unmounted: {},
  };

  return (
    <Transition nodeRef={nodeRef} in={open} timeout={500} unmountOnExit>
      {(state) => (
        <div
          ref={nodeRef}
          className="fixed top-[10px] left-0 z-1000 flex w-full"
        >
          <div
            className={clsx(
              "relative mx-[10px] flex h-[80px] w-full items-center justify-center border text-[12px] font-medium",
              {
                "border-[#86CB13] bg-[#C7E98E] text-[#56820C]":
                  variant === "success",
                "border-[#FF5C38] bg-[#FFCFC0] text-[#FF5C38]":
                  variant === "alert",
              },
            )}
            style={{
              ...defaultStyle,
              ...transitionStyles[state],
            }}
          >
            <Image
              src={
                variant === "success"
                  ? "/i/snackbar_icon_check.svg"
                  : "/i/snackbar_icon_alert.svg"
              }
              alt="success"
              width={variant === "success" ? 20 : 16}
              height={variant === "success" ? 20 : 16}
              className="mr-[8px]"
            />
            {message}
            {variant === "alert" && (
              <button
                // 閉じるだけで、押されなかったことに情報が無い
                data-analytics-ignore
                onClick={hideSnackbar}
                className="absolute top-[20px] right-[20px] p-[10px]"
              >
                <Image
                  src="/i/snackbar_alert_close_icon.svg"
                  alt="close"
                  width={15}
                  height={15}
                />
              </button>
            )}
          </div>
        </div>
      )}
    </Transition>
  );
}

export const SnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [variant, setVariant] = useState<SnackbarVariant>("success");

  const showSnackbar = ({
    message,
    variant = "success",
    duration = 3800,
  }: {
    message: string;
    variant?: SnackbarVariant;
    duration?: number;
  }) => {
    setMessage(message);
    setVariant(variant);
    setOpen(true);
    if (variant === "success") {
      setTimeout(() => {
        setOpen(false);
      }, duration);
    }
  };

  const hideSnackbar = () => {
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, hideSnackbar }}>
      {children}
      <Snackbar
        open={open}
        message={message}
        variant={variant}
        hideSnackbar={hideSnackbar}
      />
    </SnackbarContext.Provider>
  );
};

export function useSnackbar(): SnackbarContextValue["showSnackbar"] {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("SnackbarContext is not provided");
  }
  return context.showSnackbar;
}
